import { useEffect, useRef, useState } from "react";
import { ChevronDown, ExternalLink, Layout } from "react-feather";
import { toast } from "react-hot-toast";
import { Button } from "../../../../components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ConfirmationModal } from "../../../../components/ui/confirmation-modal";
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogFooter,
} from "../../../../components/ui/dialog";
import { ImageUploader } from "../../../../components/ImageUploader/ImageUploader";
import { storageService } from "../../../../services/storage.service";
import {
  whiteLabelService,
  SlugAvailability,
  WhiteLabelStateView,
} from "../../../../services/whiteLabel.service";

/**
 * The brand's white-label transition.
 *
 * Three states, driven entirely by `status` from the server:
 *
 *   NONE     nothing to show — this brand hasn't been enabled for white-label.
 *   PENDING  enabled, but no address chosen. Shows the activation form. Until
 *            it is submitted the brand behaves as an ordinary brand, so there
 *            is nothing to undo if they never finish.
 *   LIVE     shows the portal address and allows changing it.
 *
 * The portal address carries every link affiliates share, so choosing it (and
 * especially changing it — that breaks links already in the wild) is gated
 * behind two explicit steps: an "are you sure" warning, then typing the exact
 * subdomain back — the same guard rail as destructive deletes, because a typo
 * here still goes live.
 *
 * Self-contained on purpose: it talks to `/white-label/me`, a different
 * resource than the appearance PUT the tab's main Save button fires, so wiring
 * it into that button would couple two endpoints under one click.
 */
type SlugConfirmStep = null | "warn" | "type";

export function PortalBrandingSection() {
  const [open, setOpen] = useState(true);
  const [state, setState] = useState<WhiteLabelStateView | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  const [slug, setSlug] = useState("");
  const [logo, setLogo] = useState("");
  const [availability, setAvailability] = useState<SlugAvailability | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [justActivated, setJustActivated] = useState(false);
  const [slugConfirmStep, setSlugConfirmStep] = useState<SlugConfirmStep>(null);
  const [slugConfirmTyped, setSlugConfirmTyped] = useState("");
  // Activation only: whether to email existing affiliates about the portal.
  // Their accounts are untouched either way, so this is the brand's call.
  const [notifyAffiliates, setNotifyAffiliates] = useState(true);

  useEffect(() => {
    let cancelled = false;
    whiteLabelService
      .getMyConfig()
      .then((view) => {
        if (cancelled) return;
        setState(view);
        setSlug(view.portalSlug ?? view.suggestedSlug ?? "");
        setLogo(view.portalLogo ?? "");
      })
      .catch(() => !cancelled && setLoadFailed(true));
    return () => {
      cancelled = true;
    };
  }, []);

  // Debounced availability check so the brand finds out an address is taken
  // while typing rather than on submit.
  const checkSeq = useRef(0);
  useEffect(() => {
    const candidate = slug.trim().toLowerCase();
    if (!candidate || candidate === state?.portalSlug) {
      setAvailability(null);
      return;
    }
    const seq = ++checkSeq.current;
    setIsChecking(true);
    const timer = setTimeout(() => {
      whiteLabelService
        .checkSlug(candidate)
        .then((result) => {
          // Ignore a response that a later keystroke has already superseded.
          if (seq === checkSeq.current) setAvailability(result);
        })
        .catch(() => seq === checkSeq.current && setAvailability(null))
        .finally(() => seq === checkSeq.current && setIsChecking(false));
    }, 350);
    return () => clearTimeout(timer);
  }, [slug, state?.portalSlug]);

  const handleLogoUpload = async (file: File) => {
    setIsUploading(true);
    try {
      setLogo(await storageService.uploadImage(file, "portal-logos"));
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to upload logo"
      );
    } finally {
      setIsUploading(false);
    }
  };

  const closeSlugConfirm = () => {
    setSlugConfirmStep(null);
    setSlugConfirmTyped("");
  };

  const isPending = state?.status === "PENDING";
  const candidate = slug.trim().toLowerCase();
  // Comes from the server so the form shows what this deploy actually serves:
  // reveshare.com in production, staging.reveshare.com on staging. Hardcoding
  // it meant staging promised brands a production address.
  const portalDomain = state?.portalDomain ?? "reveshare.com";
  const addressChanged = !isPending && candidate !== state?.portalSlug;

  /** The write behind the confirmation flow: activate, or move the address. */
  const handleConfirmedSubmit = async () => {
    setIsSaving(true);
    try {
      const view = isPending
        ? await whiteLabelService.activate({
            portalSlug: candidate,
            ...(logo ? { portalLogo: logo } : {}),
            notifyAffiliates,
          })
        : await whiteLabelService.update({ portalSlug: candidate, portalLogo: logo });
      setState(view);
      setAvailability(null);
      // Both activation and a move invalidate links in the wild, so both get
      // the "re-share your links" notice rather than a quiet "Saved".
      setJustActivated(true);
      closeSlugConfirm();
    } catch (error: any) {
      // Surface the backend's reason (address just taken, reserved…) — for a
      // choice this consequential, "failed" alone isn't actionable.
      toast.error(
        error?.response?.data?.message ||
          (error instanceof Error
            ? error.message
            : "Could not save your portal address")
      );
      // A race on the address (someone claimed it mid-confirmation) should
      // land the brand back on the form, not inside a stale dialog.
      closeSlugConfirm();
    } finally {
      setIsSaving(false);
    }
  };

  /** Logo-only save — no address consequences, so no confirmation. */
  const handleSaveLogo = async () => {
    setIsSaving(true);
    try {
      const view = await whiteLabelService.update({ portalLogo: logo });
      setState(view);
      toast.success("Portal updated");
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          (error instanceof Error
            ? error.message
            : "Could not update your portal")
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (loadFailed || !state || state.status === "NONE") return null;

  const canSubmit =
    !isSaving &&
    !isUploading &&
    !isChecking &&
    candidate.length > 0 &&
    (candidate === state.portalSlug || availability?.available === true);
  const logoChanged = logo !== (state.portalLogo ?? "");
  const needsConfirm = isPending || addressChanged;

  return (
    <div className="border border-border rounded-xl bg-card overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 text-left"
      >
        <span className="flex items-center gap-2.5">
          <Layout size={18} className="text-muted-foreground" />
          <span className="font-medium text-foreground">Affiliate portal</span>
          <Badge variant={isPending ? "secondary" : "default"}>
            {isPending ? "Setup required" : "Live"}
          </Badge>
        </span>
        <ChevronDown
          size={18}
          className={`text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-5 border-t border-border pt-5">
          {justActivated ? (
            <div className="rounded-lg border border-border bg-muted/40 p-4 space-y-2">
              <p className="font-medium text-foreground">
                Your portal is live at {state.portalUrl}
              </p>
              <p className="text-sm text-muted-foreground">
                Your join links have changed. Affiliates you invite from now on
                get the new address automatically, but{" "}
                <strong className="text-foreground">
                  any link you shared previously now points at the old address
                </strong>{" "}
                — re-share your join link with your affiliates.
              </p>
              <a
                href={`${state.portalUrl}/join`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground underline underline-offset-2"
              >
                {state.portalUrl}/join <ExternalLink size={13} />
              </a>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              {isPending
                ? "Your account has been enabled for a branded affiliate portal. Choose your address to finish setting it up — until then, everything works exactly as it does now."
                : "Affiliates see this address and logo instead of Reveshare's."}
            </p>
          )}

          <div className="space-y-2">
            <Label htmlFor="portal-slug">Portal address</Label>
            <div className="flex items-center gap-0 rounded-lg border border-border overflow-hidden bg-background">
              <Input
                id="portal-slug"
                value={slug}
                onChange={(e) =>
                  // Constrain as they type: a DNS label is lowercase letters,
                  // digits and hyphens only.
                  setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))
                }
                placeholder="your-brand"
                autoComplete="off"
                spellCheck={false}
                className="border-0 focus-visible:ring-0 rounded-none font-mono"
              />
              <span className="px-3 py-2 text-sm text-muted-foreground border-l border-border whitespace-nowrap">
                .{portalDomain}
              </span>
            </div>
            <p className="text-xs min-h-[1rem]">
              {isChecking ? (
                <span className="text-muted-foreground">Checking…</span>
              ) : availability && !availability.available ? (
                <span className="text-destructive">{availability.reason}</span>
              ) : availability?.available ? (
                <span className="text-muted-foreground">
                  {availability.url} is available.
                </span>
              ) : (
                <span className="text-muted-foreground">
                  Lowercase letters, numbers and hyphens.
                </span>
              )}
            </p>
            {addressChanged && (
              <p className="text-xs">
                <span className="font-medium text-amber-600 dark:text-amber-400">
                  Changing your address breaks links you have already shared
                </span>{" "}
                <span className="text-muted-foreground">
                  — you will need to re-share your join link with your
                  affiliates.
                </span>
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Logo</Label>
            <ImageUploader
              id="portal-logo-upload"
              image={logo}
              onChange={handleLogoUpload}
              size="sm"
              shape="rectangular"
              helpText={
                isUploading
                  ? "Uploading…"
                  : "Optional — your existing brand image is used if you skip it."
              }
            />
          </div>

          {needsConfirm ? (
            <Button
              onClick={() => setSlugConfirmStep("warn")}
              disabled={!canSubmit}
            >
              {isSaving
                ? "Saving…"
                : isPending
                  ? "Set up my portal"
                  : "Save changes"}
            </Button>
          ) : (
            logoChanged && (
              <Button onClick={handleSaveLogo} disabled={isSaving || isUploading}>
                {isSaving ? "Saving…" : "Save changes"}
              </Button>
            )
          )}
        </div>
      )}

      {/* Step 1: make sure they understand what this address means for links */}
      <ConfirmationModal
        isOpen={slugConfirmStep === "warn"}
        onClose={closeSlugConfirm}
        onConfirm={() => setSlugConfirmStep("type")}
        variant="warning"
        title={
          isPending
            ? "Set your portal address"
            : "Change your portal address?"
        }
        message={
          isPending
            ? "Your affiliates' portal will live at the address below, and every join link you share will point at it. Changing it later breaks links already in the wild, so check it carefully now."
            : "Every link you have already shared will stop working and keep pointing at your old address. Affiliates you invite from now on get the new address, but you will need to re-share your join link with everyone else."
        }
        itemName={`${candidate}.${portalDomain}`}
        footnote="Next, you'll be asked to type the subdomain to confirm it's exactly what you want."
        confirmText="I understand, continue"
      />

      {/* Step 2: type it back — a typo here would go live */}
      <DialogRoot
        open={slugConfirmStep === "type"}
        onOpenChange={(o) => !o && !isSaving && closeSlugConfirm()}
      >
        <DialogContent>
          <DialogHeader showCloseButton onClose={closeSlugConfirm}>
            Confirm your portal address
          </DialogHeader>

          <p className="text-sm text-muted-foreground">
            To {isPending ? "set" : "move"} your portal address to{" "}
            <span className="font-medium text-foreground">
              {candidate}.{portalDomain}
            </span>
            , type{" "}
            <span className="font-mono font-semibold text-foreground">
              {candidate}
            </span>{" "}
            below. Check it carefully — this is the address your affiliates
            will use.
          </p>

          {isPending && (
            <div className="mt-4 rounded-lg border border-border bg-muted/40 p-3 text-sm">
              <p className="font-medium text-foreground">
                What happens to your existing affiliates
              </p>
              <ul className="mt-2 space-y-1 text-muted-foreground list-disc pl-5">
                <li>
                  They keep their Reveshare account, their other brands and
                  their login. Nothing is moved or removed.
                </li>
                <li>
                  They can also sign in at your portal, where they see only
                  your program, earnings and payouts.
                </li>
                <li>
                  New affiliates who join through your portal see your portal
                  only.
                </li>
              </ul>
              <label className="mt-3 flex items-start gap-2 text-foreground">
                <input
                  type="checkbox"
                  checked={notifyAffiliates}
                  onChange={(e) => setNotifyAffiliates(e.target.checked)}
                  disabled={isSaving}
                  className="mt-0.5"
                />
                <span>
                  Email my existing affiliates about the new portal address
                </span>
              </label>
            </div>
          )}

          <Input
            value={slugConfirmTyped}
            onChange={(e) => setSlugConfirmTyped(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && slugConfirmTyped === candidate) {
                void handleConfirmedSubmit();
              }
            }}
            placeholder={candidate}
            autoFocus
            disabled={isSaving}
            className="mt-4 font-mono"
            aria-label="Type your subdomain to confirm"
          />
          {slugConfirmTyped.length > 0 && slugConfirmTyped !== candidate && (
            <p className="text-xs text-destructive mt-1.5">
              Doesn't match {candidate} yet.
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={closeSlugConfirm}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void handleConfirmedSubmit()}
              disabled={isSaving || slugConfirmTyped !== candidate}
            >
              {isSaving
                ? "Saving…"
                : isPending
                  ? "Set portal address"
                  : "Move portal address"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </div>
  );
}
